import type { ColumnDef } from '@tanstack/react-table';
import type { DetailPointTransactionEntity } from 'services/openapi';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  PointTransactionStatus,
  PointTransactionTypes,
  PointTransactionTypesColor,
  TransactionStatusColor,
} from 'configs/constants';
import { StringUtils, NumberUtils } from 'modules';
import { subMonths, endOfDay, startOfDay, format } from 'date-fns';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'configs';
import { formatDateTime } from 'utils/helpers/helpers';
import { MainRouteKeys } from 'routes/enums';

type BeneficiaryLine = {
  label?: string;
  value: string;
  tone?: 'default' | 'danger';
};

const isBeneficiaryValueVisible = (value: any) =>
  value !== undefined &&
  value !== null &&
  String(value).trim() !== '' &&
  String(value).trim() !== '--';

const createBeneficiaryLine = (
  label: string,
  value: any,
  tone: BeneficiaryLine['tone'] = 'default',
): BeneficiaryLine | null => {
  if (!isBeneficiaryValueVisible(value)) return null;

  return {
    label,
    value: String(value),
    tone,
  };
};

const normalizedPhoneNumber = (phoneNumber?: string) =>
  phoneNumber ? StringUtils.normalizedPhoneNumber(phoneNumber) : '';

const buildBankDisplayName = (bank: any) => {
  if (!bank) return '';

  const shortName = String(bank.shortName || '').trim();
  const name = String(bank.name || '').trim();

  if (shortName && name && !name.includes(shortName)) {
    return `${shortName} - ${name}`;
  }

  return shortName || name;
};

export const getBeneficiaryDisplayLines = (
  item: Partial<DetailPointTransactionEntity> & Record<string, any>,
): BeneficiaryLine[] => {
  const bank = item?.info?.bank;
  const bidvErrorDesc = item?.info?.bidvErrorDesc;

  if (bank) {
    return [
      createBeneficiaryLine('Tên:', bank.bankAccountName),
      createBeneficiaryLine('Số TK:', bank.bankAccountNumber),
      createBeneficiaryLine('NH:', buildBankDisplayName(bank)),
      createBeneficiaryLine('Lỗi:', bidvErrorDesc, 'danger'),
    ].filter(Boolean) as BeneficiaryLine[];
  }

  if (isBeneficiaryValueVisible(item?.toUserSearchString)) {
    return [{ value: String(item.toUserSearchString) }];
  }

  const toUserPhone = normalizedPhoneNumber(item?.toUserPhone);

  return isBeneficiaryValueVisible(toUserPhone) ? [{ value: toUserPhone }] : [];
};

export const getBeneficiaryExportText = (
  item: Partial<DetailPointTransactionEntity> & Record<string, any>,
) => {
  return getBeneficiaryDisplayLines(item)
    .map((line) => (line.label ? `${line.label} ${line.value}` : line.value))
    .join('\n');
};

export type FilterType = {
  transactionCode?: string;
  compId?: string;
  transactionType?:
    | 'Tất cả'
    | 'Thanh toán hóa đơn-Tiền điện'
    | 'Nạp điểm'
    | 'Chuyển điểm'
    | 'Nhận điểm'
    | 'Hoàn điểm'
    | 'Rút điểm';
  transactionStatus:
    | 'Tất cả'
    | 'Thành công'
    | 'Thất bại'
    | 'Đã hủy'
    | 'Đang chờ hủy'
    | 'Đang xử lý';
  fromTime: string | number;
  toTime: string | number;
  fromUserSearchString?: string;
  toUserSearchString?: string;
  searchDetailContent?: string;
  refreshData: number;
  pageSize: number;
  pageIndex: number;
};

export const getInitFilterValues = (urlParams: any): FilterType => {
  return {
    transactionCode: urlParams?.get('transactionCode') ?? '',
    compId: urlParams?.get('compId') ?? 'Tất cả',
    transactionStatus:
      urlParams?.get('transactionStatus') ?? PointTransactionStatus['Tất cả'],
    transactionType:
      urlParams?.get('transactionType') ?? PointTransactionTypes['Tất cả'],
    fromTime:
      urlParams?.get('fromTime') ??
      startOfDay(subMonths(new Date(), 1)).toISOString(),
    toTime: urlParams?.get('toTime') ?? endOfDay(new Date()).toISOString(),
    fromUserSearchString: urlParams?.get('fromUserSearchString') ?? '',
    toUserSearchString: urlParams?.get('toUserSearchString') ?? '',
    searchDetailContent: urlParams?.get('searchDetailContent') ?? '',
    refreshData: 0,
    pageSize: Number(urlParams?.get('pageSize') ?? DEFAULT_PAGE_SIZE),
    pageIndex: Number(urlParams?.get('pageIndex') ?? DEFAULT_PAGE_INDEX),
  };
};

// export const initFilterValues: FilterType = {
//   transactionCode: '',
//   transactionStatus: PointTransactionStatus['Tất cả'],
//   transactionType: PointTransactionTypes['Tất cả'],
//   fromTime: startOfDay(subMonths(new Date(), 1)).toISOString(),
//   toTime: endOfDay(new Date()).toISOString(),
//   fromUserSearchString: '',
//   toUserSearchString: '',
//   searchDetailContent: '',
//   refreshData: 0,
// };

const PointTransactionDetailLink = ({
  transactionCode,
}: {
  transactionCode: string;
}) => {
  const location = useLocation();
  const detailPath = MainRouteKeys.PointTransactionDetails.replace(
    ':transactionCode',
    encodeURIComponent(transactionCode),
  );

  return (
    <Link
      to={detailPath}
      state={{
        pointTransactionsList: `${location.pathname}${location.search}`,
      }}
    >
      {transactionCode}
    </Link>
  );
};

export const columnsDefined: ColumnDef<DetailPointTransactionEntity>[] = [
  {
    accessorKey: 'transactionCode',
    header: 'Mã giao dịch',
    cell: (info) => {
      const transactionCode = String(info.getValue() || '');
      return <PointTransactionDetailLink transactionCode={transactionCode} />;
    },
  },
  {
    accessorKey: 'transactionType',
    header: 'Loại giao dịch',
  },
  {
    accessorKey: 'serviceType',
    header: 'Loại dịch vụ',
  },
  {
    accessorKey: 'topUpProvider',
    header: 'Phương thức nạp',
  },
  {
    accessorKey: 'fromUserPhone',
    header: 'Bên chuyển',
    cell: (info) =>
      info.getValue()
        ? StringUtils.normalizedPhoneNumber(info.getValue() as string)
        : '',
  },
  {
    accessorKey: '',
    id: 'beneficiary',
    header: 'Bên thụ hưởng',
    meta: {
      columnSize: {
        width: 340,
        minWidth: 320,
        maxWidth: 380,
      },
    },
    cell: (info) => {
      const lines = getBeneficiaryDisplayLines(info.row.original as any);
      const tooltipContent = getBeneficiaryExportText(info.row.original as any);

      if (!lines.length) return '';

      return (
        <div className='point-transaction-beneficiary' title={tooltipContent}>
          {lines.map((line, index) =>
            line.label ? (
              <div
                key={`${line.label}-${index}`}
                className='point-transaction-beneficiary__line'
              >
                <span className='point-transaction-beneficiary__label'>
                  {line.label}
                </span>
                <span
                  className={`point-transaction-beneficiary__value ${
                    line.tone === 'danger'
                      ? 'point-transaction-beneficiary__value--danger'
                      : ''
                  }`}
                >
                  {line.value}
                </span>
              </div>
            ) : (
              <div
                key={`beneficiary-${index}`}
                title={line.value}
                className='point-transaction-beneficiary__line point-transaction-beneficiary__line--single'
              >
                {line.value}
              </div>
            ),
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'amount',
    header: 'Số tiền',
    cell: (info) => {
      return (
        <div
          className={
            PointTransactionTypesColor[
              info.row.original
                ?.transactionType as keyof typeof PointTransactionTypesColor
            ]
          }
        >
          {info.getValue()
            ? NumberUtils.toFormatNumber(Number(info.getValue()))
            : '0'}
        </div>
      );
    },
    // cell: (info) => (info.getValue() ? NumberUtils.toFormatNumber(Number(info.getValue())) : ''),
  },
  {
    accessorKey: 'transactionStatusName',
    header: 'Trạng thái',
    cell: (info) => (
      <div
        className={
          TransactionStatusColor[
            info.getValue() as keyof typeof TransactionStatusColor
          ]
        }
      >
        {String(info.getValue())}
      </div>
    ),
  },
  {
    accessorKey: 'transactionUpdAt',
    header: 'Thời gian',
    cell: (info) =>
      format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
  },
];

export const convertVariables = (variables: FilterType) => {
  const { fromTime, toTime, searchDetailContent, compId, ...rest } = variables;

  const newTimeFrom =
    typeof fromTime === 'string' ? new Date(fromTime).getTime() : fromTime;
  const newTimeTo =
    typeof toTime === 'string' ? new Date(toTime).getTime() : toTime;

  return {
    ...rest,
    ...(compId && compId !== 'Tất cả' ? { compId } : {}),
    fromTime: fromTime ? formatDateTime(Number(newTimeFrom)) : '',
    toTime: toTime ? formatDateTime(Number(newTimeTo), true) : '',
    ...(searchDetailContent ? { searchDetailContent } : {}),
    excutedOnlySelf: false,
  };
};

export const convertSelectStatusOptions = () => {
  return Object.keys(PointTransactionStatus).map((key: string) => {
    return {
      label: PointTransactionStatus[key as keyof typeof PointTransactionStatus],
      value: key,
    };
  });
};

export const convertSelectTransactionTypeOptions = () => {
  return Object.keys(PointTransactionTypes).map((key: string) => {
    return {
      label: PointTransactionTypes[key as keyof typeof PointTransactionTypes],
      value: key,
    };
  });
};
